import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../environments/environment';

import { Scheda } from '../models/scheda.model';
import { OggettoIntervento } from '../models/oggetto-intervento.model';
import { SchedaGeoJsonUtils, SchedeFeatureCollection, SchedeResponse } from '../models/scheda-geojson.model';
import { Accessibilita } from '../models/accessibilita.model';
import { CodiceATO } from '../models/codice-ato.model';
import { CreazioneSchedaDTO } from '../models/requests/scheda-creazione.model';
import { NuovoCodiceATO } from '../models/nuovo-codice-ato.model';
import { SchedaAggiornaGeometriaDTO } from '../models/requests/scheda-aggiorna-geometria.model';
import { CodiceAtoDb } from '../models/codice-ato-db.model';

@Injectable({
  providedIn: 'root'
})
export class SchedeService {
  private apiUrl = environment.apiUrl + "scheda"; // URL base per le API delle schede

  constructor(
    private http: HttpClient,
    private logger: NGXLogger
  ) { }

  getSchede(): Observable<Scheda[]> {
    const ruoloUtente = localStorage.getItem("ruolo");

    if (ruoloUtente === "ENTE_ESECUTORE") {

      const enteId = localStorage.getItem("enteId") || '';
      //this.logger.info(`[SCHEDE SERVICE] recupero le schede dell'ente esecutore ${enteId}`);

      return this.http.get<Scheda[]>(`${this.apiUrl}?enteEsecutore=${enteId}`);
    }
    return this.http.get<Scheda[]>(`${this.apiUrl}`);
  }
  getSchedeEnteEsecutore(): Observable<Scheda[]> {
    const enteId = localStorage.getItem("enteId") || '';
    //this.logger.info(`[SCHEDE SERVICE] recupero le schede dell'ente esecutore ${enteId}`);
    return this.http.get<Scheda[]>(`${this.apiUrl}?enteEsecutore=${enteId}`);
  }

  getSchedeByComune(comuneId: number): Observable<Scheda[]> {
    return this.http.get<Scheda[]>(`${this.apiUrl}/by-comune/${comuneId}`);
  }

  getGeometrieSchede(): Observable<SchedeFeatureCollection> {
    let url = `${this.apiUrl}/geometrie`;
    const ruoloUtente = localStorage.getItem("ruolo");
    if (ruoloUtente === "ENTE_ESECUTORE") {
      url = url + `?enteEsecutore=${localStorage.getItem("enteId")}`;
    }

    return this.http.get<SchedeResponse>(url).pipe(
      map(response => {
        ////this.logger.info('[SCHEDE] Geometrie schede recuperate con successo');
        return SchedaGeoJsonUtils.toFeatureCollection(response);
      }),
      catchError(error => {
        this.logger.error('[SCHEDE] Errore nel recupero delle geometrie delle schede', error);
        return throwError(() => new Error('Errore nel recupero delle geometrie delle schede'));
      })
    );
  }

  getGeometriaSingolaScheda(id: number): Observable<SchedeFeatureCollection> {
    const url = `${this.apiUrl}/${id}/geometria`;


    return this.http.get<SchedeResponse>(url).pipe(
      map(response => {
        //this.logger.info('[SCHEDE] Geometria singola scheda recuperata con successo');
        console.log(response);
        return SchedaGeoJsonUtils.toFeatureCollection(response);
      }),
      catchError(error => {
        this.logger.error('[SCHEDE] Errore nel recupero della geometria della scheda', error);
        return throwError(() => new Error('Errore nel recupero della geometria della scheda'));
      })
    );
  }

  getOggettiIntervento(): Observable<OggettoIntervento[]> {
    return this.http.get<OggettoIntervento[]>(`${this.apiUrl}/oggetti`);
  }
  getLivelliAccessibilita(): Observable<Accessibilita[]> {
    return this.http.get<Accessibilita[]>(`${this.apiUrl}/livelli-accessibilita`);
  }
  getCodiciAto(): Observable<NuovoCodiceATO[]> {
    return this.http.get<NuovoCodiceATO[]>(`${this.apiUrl}/codici-ato`);
  }

  getCodiciAtoDb(): Observable<CodiceAtoDb[]> {
    return this.http.get<CodiceAtoDb[]>(`${this.apiUrl}/codici-ato-db`);
  }

  crea(scheda: CreazioneSchedaDTO): Observable<Scheda> {
    return this.http.post<Scheda>(`${this.apiUrl}`, scheda);
  }

  modifica(scheda: Scheda | null): Observable<Scheda> {
    //this.logger.info('[SCHEDA SERVICE] Modifica chiamando url ', `${this.apiUrl}/${scheda?.id}`);
    return this.http.put<Scheda>(`${this.apiUrl}/${scheda?.id}`, scheda);
  }

  approva(schedaId: number): Observable<Scheda> {
    return this.http.put<Scheda>(`${this.apiUrl}/${schedaId}/approvazione`, {});
  }

  eliminaScheda(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => {
        this.logger.error('[SCHEDE] Errore durante l\'eliminazione della scheda', error);
        return throwError(() => new Error('Errore durante l\'eliminazione della scheda'));
      })
    );
  }

  caricaAllegato(formData: FormData) {
    return this.http.post(`${this.apiUrl}/allegati`, formData);
  }

  modificaGeometria(schedaId: number, scheda: SchedaAggiornaGeometriaDTO): Observable<any> {
    return this.http.put(`${this.apiUrl}/${schedaId}/geometria`, scheda).pipe(
      catchError(error => {
        this.logger.error('[SCHEDE] Errore durante il caricamento del GeoJSON', error);
        return throwError(() => new Error('Errore durante il caricamento del GeoJSON'));
      })
    );
  }

  getGeoJSON(schedaId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/${schedaId}/geojson`).pipe(
      catchError(error => {
        this.logger.error('[SCHEDE] Errore durante il recupero del GeoJSON', error);
        return throwError(() => new Error('Errore durante il recupero del GeoJSON'));
      })
    );
  }

/*
  getRichiesteFiltrate(stato: string | number | null, anno: string | number | null,  enteEsecutore: string | null): Observable<Richiesta[]> {
    let params = new HttpParams();

    if (anno) {
      //this.logger.info("[RICHIESTE SERVICE] Anno PASSATO: " + anno);
      params = params.append('anno', anno.toString());
    } else {
      //this.logger.info("[RICHIESTE SERVICE] Anno DEFAULT: " + new Date().getFullYear());
      params = params.append('anno', new Date().getFullYear().toString());
    }

    if (stato) {
      params = params.append('stato', stato);
    }
    if (enteEsecutore) {
      params = new HttpParams();
      params = params.append('enteEsecutore', localStorage.getItem("enteId") ?? '');
    }

    //this.logger.info("[RICHIESTE SERVICE] Params finali: " + params.toString());
    return this.http.get<Richiesta[]>(`${this.apiUrl}`, { params });
  }

  getStoricoRichiesta(richiestaId: number): Observable<StoricoRichiesta[]> {
    return this.http.get<StoricoRichiesta[]>(`${this.apiUrl}/${richiestaId}/storico`);
  }

  generaRichieste(generaRichieste: GeneraRichiesteRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/genera`, generaRichieste);
  }

  aggiornaRichiesta(richiesta: Richiesta): Observable<Richiesta> {
    return this.http.put<Richiesta>(`${this.apiUrl}/${richiesta.id}`, richiesta);
  }

  comunicaContributoRichiesta(id: number, importoContributo: number): Observable<any> {

    return this.http.patch<any>(`${this.apiUrl}/${id}/contributo`, { importoContributo });
  }

  elimina(idRichiesta:number): Observable<any> {
    const httpOptions = {
      reportProgress: true,
    };
    return this.http.delete(this.apiUrl+"/"+idRichiesta, httpOptions);
  }
 */
}
